import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Download, Mail, Loader2, CheckCircle2, BookOpen, Languages, PencilLine, Quote } from 'lucide-react';
import { Navigation } from '../components/Navigation';
import { Footer } from '../components/Footer';
import { WhatsAppButton } from '../components/WhatsAppButton';
import { LegalModal } from '../components/LegalModal';
import { faqData, legalNoticeData, cookiePolicyData } from '../data/legalContent';
import Seo from '../components/Seo';
import { REFRANES_GUIA, REFRANES_EJEMPLOS } from '../config/refranesGuia';
import { trackResourceLead } from '../lib/analytics';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const INCLUYE = [
  { icon: BookOpen, title: 'Significado en español', text: 'Una explicación clara y sencilla de lo que quiere decir cada refrán o dicho.' },
  { icon: Quote, title: 'Ejemplo de uso real', text: 'Una frase de la vida cotidiana para que veas cómo y cuándo se usa de verdad.' },
  { icon: Languages, title: 'Equivalencia en inglés y alemán', text: 'La expresión equivalente en EN y DE, no una traducción literal.' },
  { icon: PencilLine, title: 'Actividad final para practicar', text: 'Un ejercicio al final del PDF con soluciones para comprobar lo aprendido.' },
];

export default function RecursoRefranesPage() {
  const [email, setEmail] = useState('');
  const [privacy, setPrivacy] = useState(false);
  const [marketing, setMarketing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const [modalType, setModalType] = useState(null);

  // Al llegar desde la tarjeta de Recursos, el navegador conserva el scroll
  // de la página anterior: empezamos siempre arriba.
  useEffect(() => {
    if (!window.location.hash) window.scrollTo(0, 0);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const clean = email.trim().toLowerCase();
    if (!EMAIL_RE.test(clean)) {
      setError('Introduce un email válido.');
      return;
    }
    if (!privacy) {
      setError('Debes aceptar la Política de Privacidad para descargar la guía.');
      return;
    }
    setError('');
    setLoading(true);
    try {
      const res = await axios.post(
        `${API}/recursos/${REFRANES_GUIA.slug}/download`,
        { email: clean, marketing_consent: marketing },
        { responseType: 'blob' }
      );
      const blobUrl = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = '100-refranes-y-dichos-populares-en-espana.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(blobUrl), 4000);
      trackResourceLead('refranes');
      setDone(true);
    } catch (err) {
      setError('Hubo un error al preparar la descarga. Inténtalo de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white text-[#4A4A4A]">
      <Seo
        path={`/recursos/${REFRANES_GUIA.slug}`}
        fullTitle={REFRANES_GUIA.seoTitle}
        description={REFRANES_GUIA.seoDescription}
        image={REFRANES_GUIA.cover}
        type="article"
      />
      <Navigation />

      {/* Hero */}
      <section className="pt-32 pb-16 bg-gradient-to-b from-[#E8F5FA] to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <span className="text-[#F5A6A4] font-semibold text-sm uppercase tracking-wider">
              Recurso gratuito · Nivel B1–C1
            </span>
            <h1 className="text-4xl sm:text-5xl font-bold text-[#4A4A4A] mt-4 mb-6 leading-tight" data-testid="refranes-h1">
              100 refranes y dichos populares en España
            </h1>
            <p className="text-base md:text-lg text-gray-600 leading-relaxed mb-6">
              Una guía gratuita para estudiantes de español de nivel B1–C1 con 100 refranes, dichos y
              expresiones habituales en España explicados de forma clara y con ejemplos reales.
            </p>
            <div className="flex flex-wrap gap-3 mb-8">
              <span className="px-3 py-1 rounded-full text-xs font-bold border bg-orange-100 text-orange-700 border-orange-300">
                {REFRANES_GUIA.level}
              </span>
              <span className="px-3 py-1 rounded-full text-xs font-bold border bg-white text-gray-600 border-gray-200">
                {REFRANES_GUIA.format}
              </span>
              <span className="px-3 py-1 rounded-full text-xs font-bold border bg-white text-gray-600 border-gray-200">
                Vocabulario / Español real
              </span>
            </div>
            <a
              href="#descargar"
              data-testid="refranes-hero-cta"
              className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-[#A8D8EA] to-[#7DBDD9] text-white rounded-xl font-bold shadow-lg hover:shadow-xl transition-all"
            >
              <Download className="w-5 h-5" />
              Descargar gratis
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="relative mx-auto max-w-sm w-full"
          >
            <img
              src={REFRANES_GUIA.cover}
              alt="Portada del PDF gratuito: 100 refranes y dichos populares en España"
              width="1158"
              height="1638"
              className="w-full h-auto rounded-2xl shadow-2xl border border-gray-100"
              data-testid="refranes-cover"
            />
          </motion.div>
        </div>
      </section>

      {/* Qué incluye */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-base md:text-lg font-bold text-[#4A4A4A] uppercase tracking-wider mb-2">
            Qué incluye cada refrán
          </h2>
          <div className="w-16 h-1 bg-gradient-to-r from-[#A8D8EA] to-[#F5A6A4] mb-10" />
          <div className="grid md:grid-cols-2 gap-6">
            {INCLUYE.map(({ icon: Icon, title, text }) => (
              <div
                key={title}
                className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 flex gap-4"
                data-testid={`refranes-incluye-${title.split(' ')[0].toLowerCase()}`}
              >
                <div className="w-11 h-11 flex-shrink-0 rounded-xl bg-gradient-to-br from-[#A8D8EA] to-[#7DBDD9] flex items-center justify-center">
                  <Icon className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="font-bold text-[#4A4A4A] mb-1">{title}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Ejemplos */}
      <section className="py-16 bg-gradient-to-b from-[#E8F5FA] to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-base md:text-lg font-bold text-[#4A4A4A] uppercase tracking-wider mb-2">
            Algunos ejemplos de la guía
          </h2>
          <div className="w-16 h-1 bg-gradient-to-r from-[#A8D8EA] to-[#F5A6A4] mb-4" />
          <p className="text-sm text-gray-600 mb-10 max-w-2xl">
            Estos son 6 de los 100 refranes y dichos que encontrarás dentro del PDF completo.
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {REFRANES_EJEMPLOS.map((r, i) => (
              <div
                key={r.refran}
                data-testid={`refranes-ejemplo-${i}`}
                className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 hover:shadow-2xl transition-all duration-300"
              >
                <h3 className="text-lg font-bold text-[#4A4A4A] mb-3 leading-snug">«{r.refran}»</h3>
                <p className="text-sm text-gray-600 mb-3">{r.significado}</p>
                <p className="text-sm text-gray-500 italic mb-4">{r.ejemplo}</p>
                <div className="space-y-1 text-xs text-gray-500 border-t border-gray-100 pt-3">
                  <p><span className="font-bold text-[#7DBDD9]">EN</span> {r.en}</p>
                  <p><span className="font-bold text-[#F5A6A4]">DE</span> {r.de}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Formulario de descarga */}
      <section id="descargar" className="py-16">
        <div className="max-w-2xl mx-auto px-4 sm:px-6">
          <div className="bg-white rounded-3xl shadow-2xl border border-gray-100 overflow-hidden">
            <div className="bg-gradient-to-r from-[#E8F5FA] to-[#FFF0EF] p-6">
              <h2 className="text-base md:text-lg font-bold text-[#4A4A4A]">Descarga gratis la guía</h2>
              <p className="text-sm text-gray-600 mt-1">
                Recibe la guía completa con 100 refranes y dichos populares en España.
              </p>
            </div>

            {done ? (
              <div className="p-8 text-center" data-testid="refranes-success">
                <CheckCircle2 className="w-12 h-12 text-[#7DBDD9] mx-auto mb-4" />
                <p className="text-lg font-bold text-[#4A4A4A] mb-2">¡Listo! Ya puedes disfrutar de tu guía.</p>
                <p className="text-sm text-gray-600">
                  Si la descarga no ha empezado automáticamente, vuelve a pulsar el botón.
                </p>
                <button
                  onClick={() => setDone(false)}
                  data-testid="refranes-download-again"
                  className="mt-6 inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#A8D8EA] to-[#7DBDD9] text-white rounded-xl font-semibold text-sm shadow-md hover:shadow-lg transition-all"
                >
                  <Download className="w-4 h-4" />
                  Descargar de nuevo
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="p-6 space-y-4" data-testid="refranes-form">
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="tu@email.com"
                    aria-label="Correo electrónico"
                    inputMode="email"
                    autoComplete="email"
                    required
                    data-testid="refranes-email-input"
                    className="w-full pl-11 pr-4 py-3 rounded-xl border border-gray-200 focus:border-[#A8D8EA] focus:ring-2 focus:ring-[#A8D8EA]/30 outline-none"
                  />
                </div>

                <label className="flex items-start gap-3 text-sm text-gray-600 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={privacy}
                    onChange={(e) => setPrivacy(e.target.checked)}
                    data-testid="refranes-privacy-checkbox"
                    className="mt-0.5 w-4 h-4 accent-[#7DBDD9] flex-shrink-0"
                  />
                  <span>
                    He leído y acepto la{' '}
                    <button
                      type="button"
                      onClick={() => setModalType('cookies')}
                      data-testid="refranes-privacy-link"
                      className="text-[#7DBDD9] font-semibold underline hover:text-[#5DA8CC]"
                    >
                      Política de Privacidad
                    </button>
                    .
                  </span>
                </label>

                <label className="flex items-start gap-3 text-sm text-gray-600 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={marketing}
                    onChange={(e) => setMarketing(e.target.checked)}
                    data-testid="refranes-marketing-checkbox"
                    className="mt-0.5 w-4 h-4 accent-[#7DBDD9] flex-shrink-0"
                  />
                  <span>
                    Quiero recibir por email nuevos recursos gratuitos, consejos para aprender español y
                    novedades de Spanisch en la Red.
                  </span>
                </label>

                {error && (
                  <p className="text-sm text-red-500 font-semibold" data-testid="refranes-error">{error}</p>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  data-testid="refranes-submit-btn"
                  className="w-full py-3.5 bg-gradient-to-r from-[#A8D8EA] to-[#7DBDD9] text-white rounded-xl font-bold shadow-lg hover:shadow-xl transition-all disabled:opacity-60 flex items-center justify-center gap-2"
                >
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Download className="w-5 h-5" />}
                  {loading ? 'Preparando tu guía...' : 'Descargar gratis'}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      <Footer />
      <WhatsAppButton />
      <LegalModal
        isOpen={modalType !== null}
        onClose={() => setModalType(null)}
        type={modalType}
        faqData={faqData}
        legalNoticeData={legalNoticeData}
        cookiePolicyData={cookiePolicyData}
      />
    </div>
  );
}
